
import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  life: number;
  maxLife: number;
}

const MouseParticles: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particles = useRef<Particle[]>([]);
  const lastPos = useRef({ x: 0, y: 0 });
  const frameId = useRef<number>(0);
  const lastTime = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Apenas cria se o movimento for significativo
      if (dist > 60) { 
        createParticle(e.clientX, e.clientY);
        lastPos.current = { x: e.clientX, y: e.clientY };
      }
    };

    const createParticle = (x: number, y: number) => {
      // Menos partículas simultâneas
      if (particles.current.length > 20) return; 

      const maxLife = 10 + Math.random() * 5;
      particles.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        size: Math.random() * 0.6 + 0.1,
        color: Math.random() > 0.8 ? '#c084fc' : '#ffffff',
        alpha: 0.4,
        life: maxLife,
        maxLife: maxLife
      });
    };

    const animate = (timestamp: number) => {
      // Limitador de FPS (30 FPS é suficiente para este efeito e poupa GPU)
      if (timestamp - lastTime.current < 33) {
        frameId.current = requestAnimationFrame(animate);
        return;
      }
      lastTime.current = timestamp;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const pArr = particles.current;
      for (let i = pArr.length - 1; i >= 0; i--) {
        const p = pArr[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life--;
        p.alpha = (p.life / p.maxLife) * 0.3;

        if (p.life <= 0) {
          pArr.splice(i, 1);
          continue;
        }

        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      
      frameId.current = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    resize();
    frameId.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(frameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[1000]"
      style={{ opacity: 0.4, transform: 'translateZ(0)' }}
    />
  );
};

export default MouseParticles;
